import {
	Component, OnInit, OnDestroy, Input, Output,
	EventEmitter, ViewChild, ElementRef, AfterViewInit, DestroyRef, inject
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { combineLatest, interval, Subject } from 'rxjs';
import { distinctUntilChanged, filter, finalize, takeUntil, tap } from 'rxjs/operators';

import { LevelAsset, AssetsDataService, ILevelAsset } from '@services/assets-data.service';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { GlobalFunctions, SnackType, imagePath, imgTypes } from '@global/GlobalFunctions';
import { EditHierarchyLocationComponent } from '@dialogs/edit-hierarchy-location/edit-hierarchy-location.component';
import { IOrganisationsHierarchy, OrganisationDataService } from '@services/organisation-data.service';

/**
 * Component
 */
@Component({
	selector: 'app-asset-tiles-group',
	templateUrl: './asset-tiles-group.component.html',
	styleUrls: ['./asset-tiles-group.component.scss']
})
export class AssetTilesGroupComponent implements OnInit, OnDestroy, AfterViewInit {

	// -------------------------------------------------------------------------
	// Component State and Properties
	// -------------------------------------------------------------------------

	/** Observable Subject Finalise */
	private destroyRef = inject(DestroyRef);

	/** Observable Subject Finalise */
	private tileFinalise = new Subject<void>();

	/** inputSubLocation */
	@Input() inputSubLocation: IOrganisationsHierarchy;

	/** Site admin */
	@Input() AdminSite: boolean;

	/** selected */
	@Output() selected: EventEmitter<ILevelAsset> = new EventEmitter<ILevelAsset>();

	/** hideButtons */
	@Output() hideButtons = new EventEmitter<boolean>();

	/** selectedAsset */
	public selectedAsset?: ILevelAsset;

	/** accountGUID */
	public accountGUID: string;

	/** OrganisationUID */
	public OrganisationUID: string;

	/** Editing the Sensor Tiles */
	public sensorEdit: boolean = false;

	/** Assets List */
	public locationAssets: LevelAsset[] = [];

	/** isFullScreen */
	public isFullScreen: boolean = false;

	/** Basic device type check */
	public isMobileView: boolean = false;

	public top: number;
	public left: number;
	public width: number;
	public height: number;

	public textSize: number;
	public iconSize: number;

	public showWatchIcons: boolean = true;

	/** startingWidth */
	public startingWidth: string = "100%";

	/** startingHeight */
	public startingHeight: string = "calc(100% - 20px)";

	/** componentWidth */
	public componentWidth: string = this.startingWidth;

	/** componentHeight */
	public componentHeight: string = this.startingHeight;

	/** Original tile positions, used to restore positions on Cancel */
	private originalTilePositions = new Map<HTMLElement, {
		left: string;
		top: string;
	}>();

	/** Currently dragged tile */
	private draggedTile?: HTMLElement;

	/** Drag starting coordinates */
	private dragStartX = 0;
	private dragStartY = 0;
	private tileStartX = 0;
	private tileStartY = 0;

	/** Canvas containing the asset tiles */
	private tileCanvas?: HTMLElement;

	/** Bound handlers so they can be removed reliably */
	private pointerMoveHandler = (event: PointerEvent) => this.onTilePointerMove(event);
	private pointerUpHandler = (event: PointerEvent) => this.onTilePointerUp(event);
	private pointerDownHandler = (event: PointerEvent) => this.onTilePointerDown(event);

	/** View references */
	@ViewChild('container', { static: false })
	containerElement!: ElementRef<HTMLElement>;

	@ViewChild('image', { static: false })
	imageElement!: ElementRef<HTMLImageElement>;

	private resizeObserver!: ResizeObserver;

	private blnRender: boolean = false;

	/** counter used to ensure hierarchy only set once */
	private counter: number;

	/** number of asset tiles */
	private assetTilesCount: number;

	// -------------------------------------------------------------------------
	// Constructor and Subscriptions
	// -------------------------------------------------------------------------

	/**
	 * Default Constructor
	 * @param organisationDataService Organisation Data
	 * @param assetsDataService AssetsDataService
	 * @param siteSubscriptions site Subscriptions
	 * @param globalFunctions global Functions
	 * @param dialog dialog
	 */
	constructor(
		public organisationDataService: OrganisationDataService,
		public assetsDataService: AssetsDataService,
		private siteSubscriptions: siteSubscriptions,
		private globalFunctions: GlobalFunctions,
		public dialog: MatDialog,
	) {
		try {
			combineLatest([
				this.siteSubscriptions.selectedAccountGUID$.pipe(distinctUntilChanged()),
				this.siteSubscriptions.selectedOrganisationUID$.pipe(distinctUntilChanged())
			]).pipe(
				filter(([accountGUID, organisationUID]) => !!accountGUID && !!organisationUID),
				takeUntilDestroyed(this.destroyRef)
			).subscribe({
				next: ([accountGUID, organisationUID]) => {
					this.accountGUID = accountGUID;
					this.OrganisationUID = organisationUID;
				},
				error: (error) => {
					this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
				}
			});

			this.siteSubscriptions.selectedAsset$.pipe(
				distinctUntilChanged(),
				tap(() => {
					this.selectedAsset = undefined;
				}),
				filter((asset) => !!asset),
				takeUntilDestroyed(this.destroyRef)
			).subscribe({
				next: (asset_s) => {
					try {
						this.selectedAsset = typeof asset_s === 'string'
							? JSON.parse(asset_s)
							: asset_s;
					} catch (error) {
						this.globalFunctions.HandleError(error, "");
					}
				}
			});
		} catch (error) {
			this.globalFunctions.HandleError(error, "Error initialising asset subscriptions");
		}
	}

	// -------------------------------------------------------------------------
	// Lifecycle
	// -------------------------------------------------------------------------

	/**
	 * Page On Initialise Event Handler
	 */
	ngOnInit(): void { }

	/** ngAfterViewInit */
	ngAfterViewInit() {

		let refreshTime = 0.5;
		let refreshInterval = interval((refreshTime * 1000)).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			if (this.blnRender) {
				this.getImagePosition(); // Recalculate position on resize
				// this.scaleTextSize();
			}
			this.blnRender = false;
		});

		// Set up Resize Observer
		this.resizeObserver = new ResizeObserver(() => {
			this.blnRender = true;
		});

		// Observe the image element
		if (this.imageElement) {
			this.resizeObserver.observe(this.imageElement.nativeElement);
		}

		const userAgent = window.navigator.userAgent;
		// Basic device type check based on user agent
		this.isMobileView = /Mobi|Android|iPhone|iPad|iPod/.test(userAgent);

		document.addEventListener("fullscreenchange", () => {
			if (document.fullscreenElement) {
				this.isFullScreen = true;
				this.hideButtons.emit(true);
			} else {
				this.isFullScreen = false;
				this.hideButtons.emit(false);
			}
		});
	}

	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {
			this.tileFinalise.next();
			this.tileFinalise.complete();

			if (this.resizeObserver) {
				this.resizeObserver.unobserve(this.imageElement.nativeElement);
			}
		}
		catch { }

	}

	// -------------------------------------------------------------------------
	// Image Position and Display
	// -------------------------------------------------------------------------

	/** Path to Sub Location Images */
	public imagePathSubLocations(): string {
		return imagePath(this.accountGUID, imgTypes.sublocations).toLowerCase()
	}

	/**
	 * imagePathOrganisations
	 * @returns 
	 */
	public imagePathOrganisations(): string {
		return imagePath(this.accountGUID, imgTypes.organisations).toLowerCase()
	}

	/**
	 * getImagePosition
	 */
	getImagePosition() {
		if (this.imageElement) {
			const rect = this.imageElement.nativeElement.getBoundingClientRect();
			const parentElement = this.imageElement.nativeElement.parentElement;

			// Get the bounding rect of the parent element
			const parentRect = parentElement?.getBoundingClientRect();

			if (parentRect) {
				// Calculate the position relative to the parent
				this.top = rect.top - parentRect.top;
				this.left = rect.left - parentRect.left;
				this.width = rect.width;
				this.height = rect.height;

				let area = this.width * this.height;

				if (area <= 65000) {
					this.showWatchIcons = false;
				} else {
					this.showWatchIcons = true;
				}
			} else {
				console.error('Parent element not found.');
			}
		} else {
			console.error('Image element not found.');
		}
	}

	/**
	 * Used to catch any tile positions that are less then zero
	 * @param position
	 * @returns
	 */
	getSafePosition(position: number): number {
		return position < 0 ? 0 : position;
	}

	// -------------------------------------------------------------------------
	// Tile Repositioning and Pointer Events
	// -------------------------------------------------------------------------

	/**
	 * Initialise the movement of asset tiles.
	 */
	moveSensor(): void {
		if (this.sensorEdit) {
			return;
		}

		const container = this.containerElement?.nativeElement;
		const tileCanvas = container?.querySelector<HTMLElement>('.tiles-canvas');

		if (!container || !tileCanvas) {
			console.error('Asset tile canvas was not found.');
			return;
		}

		this.tileCanvas = tileCanvas;
		this.originalTilePositions.clear();

		tileCanvas.querySelectorAll<HTMLElement>('.vib-sensor').forEach(tile => {
			this.originalTilePositions.set(tile, {
				left: tile.style.left,
				top: tile.style.top
			});
		});

		this.sensorEdit = true;

		this.attachTilePointerHandlers();
	}

	/**
	 * Start dragging an asset tile.
	 * Call this from the container's pointerdown event.
	 * @param event 
	 * @returns 
	 */
	onTilePointerDown(event: PointerEvent): void {
		if (!this.sensorEdit || event.button !== 0) {
			return;
		}

		const target = event.target as HTMLElement;
		const tile = target.closest<HTMLElement>('.vib-sensor');

		if (!tile || !this.tileCanvas?.contains(tile)) {
			return;
		}

		const canvasRect = this.tileCanvas.getBoundingClientRect();
		const tileRect = tile.getBoundingClientRect();

		this.draggedTile = tile;

		this.dragStartX = event.clientX;
		this.dragStartY = event.clientY;

		this.tileStartX = tileRect.left - canvasRect.left;
		this.tileStartY = tileRect.top - canvasRect.top;

		tile.classList.add('dragging');

		// Capture the pointer so dragging continues outside the tile.
		tile.setPointerCapture(event.pointerId);

		event.preventDefault();
	}

	/**
	 * Move the selected tile within the canvas bounds.
	 * @param event 
	 * @returns 
	 */
	private onTilePointerMove(event: PointerEvent): void {
		if (!this.draggedTile || !this.tileCanvas) {
			return;
		}

		const canvasRect = this.tileCanvas.getBoundingClientRect();
		const tile = this.draggedTile;

		const maxX = Math.max(0, canvasRect.width - tile.offsetWidth);
		const maxY = Math.max(0, canvasRect.height - tile.offsetHeight);

		const x = Math.max(0, Math.min(
			maxX,
			this.tileStartX + event.clientX - this.dragStartX
		));

		const y = Math.max(0, Math.min(
			maxY,
			this.tileStartY + event.clientY - this.dragStartY
		));

		tile.style.left = `${x / canvasRect.width * 100}%`;
		tile.style.top = `${y / canvasRect.height * 100}%`;
	}

	/**
	 * Finish dragging.
	 */
	private onTilePointerUp(event: PointerEvent): void {
		if (!this.draggedTile) {
			return;
		}

		this.draggedTile.classList.remove('dragging');
		this.draggedTile = undefined;
	}

	/** attachTilePointerHandlers */
	private attachTilePointerHandlers(): void {
		this.tileCanvas?.addEventListener(
			'pointerdown',
			this.pointerDownHandler
		);

		document.addEventListener('pointermove', this.pointerMoveHandler);
		document.addEventListener('pointerup', this.pointerUpHandler);
		document.addEventListener('pointercancel', this.pointerUpHandler);
	}

	/** detachTilePointerHandlers */
	private detachTilePointerHandlers(): void {
		this.tileCanvas?.removeEventListener(
			'pointerdown',
			this.pointerDownHandler
		);

		document.removeEventListener('pointermove', this.pointerMoveHandler);
		document.removeEventListener('pointerup', this.pointerUpHandler);
		document.removeEventListener('pointercancel', this.pointerUpHandler);
	}

	// -------------------------------------------------------------------------
	// Tile Position Persistence
	// -------------------------------------------------------------------------

	/**
	 * Save the position of the Sensor Tiles
	 * @param save 
	 * @returns 
	 */
	savePositions(save: boolean): void {
		if (!this.sensorEdit || !this.tileCanvas) {
			return;
		}

		try {
			// Finish any active drag.
			this.draggedTile?.classList.remove('dragging');
			this.draggedTile = undefined;

			const canvasRect = this.tileCanvas.getBoundingClientRect();

			if (save && canvasRect.width > 0 && canvasRect.height > 0) {
				this.tileCanvas.querySelectorAll<HTMLElement>('.vib-sensor')
					.forEach(tile => {
						const original = this.originalTilePositions.get(tile);

						if (!original) {
							return;
						}

						// Skip tiles whose positions have not changed.
						if (
							tile.style.left === original.left &&
							tile.style.top === original.top
						) {
							return;
						}

						const asset = this.inputSubLocation.Assets.find(
							(x: LevelAsset) => x.AssetID.toString() === tile.id
						);

						if (!asset) {
							return;
						}

						const leftPercent = parseFloat(tile.style.left);
						const topPercent = parseFloat(tile.style.top);

						if (!Number.isFinite(leftPercent) ||
							!Number.isFinite(topPercent)) {
							return;
						}

						asset.LeftPosition = leftPercent;
						asset.TopPosition = topPercent;

						this.updateTilePosition(
							this.accountGUID,
							this.OrganisationUID,
							asset
						);
					});
			} else {
				// Cancel: restore the original inline CSS positions.
				this.originalTilePositions.forEach((position, tile) => {
					tile.style.left = position.left;
					tile.style.top = position.top;
				});
			}
		} finally {
			this.detachTilePointerHandlers();
			this.originalTilePositions.clear();
			this.tileCanvas = undefined;
			this.sensorEdit = false;
		}
	}

	/**
	 * Update System Tile information
	 * @param accountGUID 
	 * @param organisationUID 
	 * @param asset 
	 */
	updateTilePosition(accountGUID: string, organisationUID: string, asset: any) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.assetsDataService.patchOrganisationAssets$(accountGUID, organisationUID, asset).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((response) => !!response),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (response) => {
				try {
					// this.counter++; // increment counter for each successful tile position update
					// if (this.counter == this.assetTilesCount) { // only set hierarchy after the final tile update
					// 	this.subscribeOrganisationStructure(this.accountGUID, this.OrganisationUID);
					// }
				}
				catch (error) { this.globalFunctions.HandleError(error, "") }
			},
			error: (error) => {
				console.error(error)
				this.globalFunctions.HandleError(error, "updateSystemsGroups: Error updating system group info")
			},
			complete: () => { }
		});
	}

	// -------------------------------------------------------------------------
	// Organisation Structure
	// -------------------------------------------------------------------------

	/**
	 * Get the Organisation Structure
	 * @param AccountID AccountID
	 * @param OrganisationID OrganisationID
	 */
	subscribeOrganisationStructure(accountGUID: string, organisationUID: string) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		try {
			/** Get the Organisation for the selection */
			this.organisationDataService.getOrganisationStructure$(accountGUID, organisationUID).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((structure) => !!structure),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: (structure: any) => {
					try {
						/** Update the Organisation */
						var OrganisationStructure = <IOrganisationsHierarchy[]>JSON.parse(structure);
						this.siteSubscriptions.SethierarchicalData(OrganisationStructure);
					} catch (error) { console.error(error); }
				},
				error: (error) => {
					if (error["status"] != 404) {
						this.globalFunctions.HandleError(error, "Error: OrganisationData.getOrganisationStructure$(OrganisationUID).subscribe$", false)
					}
				},
				//complete: () => { console.log("OrganisationData.getOrganisationStructure$(OrganisationUID).subscribe: Complete"); }
			});
		} catch (error) { console.error(error); }
	}

	// -------------------------------------------------------------------------
	// Asset Selection
	// -------------------------------------------------------------------------

	/**
	 * Bind the Select event
	 * @param asset
	 */
	public assetSelect(asset: ILevelAsset) {
		if (this.sensorEdit) { return; }

		try {
			if (asset != this.selectedAsset) {
				this.selectedAsset = asset;
				this.siteSubscriptions.SetSelectedAsset(asset);
			}
		} catch (error) { console.error(error); }
	}

	// -------------------------------------------------------------------------
	// Location Editing
	// -------------------------------------------------------------------------

	/**
	 * Edit the Location details
	 * @param editLocation
	 */
	editLocation(editLocation: IOrganisationsHierarchy): void {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		// Create a 'Local'/shallow copy of the Location Information
		var _edit = { ...editLocation };

		// Display the dialog box
		const dialogRef = this.dialog.open(EditHierarchyLocationComponent, {
			data: _edit,
		});

		// Update the original Location with the result
		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {
			// console.log('The dialog was closed');
			// Check we have a result to work with
			this.globalFunctions.displaySpinner(true)
			Object.assign(_edit, result);
			this.organisationDataService.patchOrganisationLocation$(this.accountGUID, this.OrganisationUID, _edit).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((response) => !!response),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: (response) => {
					Object.assign(editLocation, result);
					this.subscribeOrganisationStructure(this.accountGUID, this.OrganisationUID)
					this.globalFunctions.DisplaySnackBar({
						message: "Location Details Updated",
						type: SnackType.Info,
						duration: 1000
					});
				},
				error: (error) => { this.globalFunctions.HandleError(error, "asset-tiles Error: this.organisationDataService.patchOrganisationLocation$", false) },
				complete: () => { this.globalFunctions.displaySpinner(false) }
			});
		});
	}

	// -------------------------------------------------------------------------
	// Fullscreen
	// -------------------------------------------------------------------------

	/**
	 * trueFullscreen
	 * @param element element
	 * @param fullscreen boolean
	 */
	trueFullscreen(element: HTMLElement, fullscreen: boolean) {
		let elements: HTMLElement[] = [element, element.parentElement!, element.parentElement?.parentElement!];
		let targetElement = elements.find((x: HTMLElement) => x.classList.contains("leftcont"));
		if (targetElement) {
			if (fullscreen) { targetElement.requestFullscreen(); }
			else { document.exitFullscreen(); }
		}
	}
}
